import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { networkApi, type AdSiteRow } from '../../api/network.js';
import { EmptyBox, ErrorBox, Field, Pagination, SearchInput, Spinner } from '../../components/ui.js';
import { datetime, text, truncate } from '../../lib/format.js';
import { useUrlSearch, useUrlUpdate } from '../../lib/hooks.js';
import {
  AD_TYPE_LABELS,
  CodeBadges,
  DetailModal,
  HOST_TYPE_LABELS,
  HostLink,
  PER_PAGE,
  descendingNumber,
  labelOf,
} from './shared.js';

/**
 * 광고 사이트 — 레거시 /xadm/report_network_referer_site.php.
 *
 * 리퍼러 도메인을 사람이 분류해 둔 표다(ALK_network_site, 330행).
 * 리퍼러 목록·통계 화면이 이 표를 도메인으로 맞춰 분류를 같이 보여준다.
 *
 * 원본의 등록·수정 팝업(network_site_add/modify.php)은 이번 범위 밖이라
 * 읽기만 한다. 원본 검색칸은 값이 늘 비어 보이는 버그가 있었다
 * (`value="<?=$partnerse?>"` - 이 화면에 없는 변수를 찍었다) - 여기서는
 * 지금 걸린 검색어가 그대로 보인다.
 */

export function AdSitesPage() {
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);
  const [q, setQ] = useUrlSearch(params, update, 'q');
  const page = Number(params.get('page') || '1');
  const [detail, setDetail] = useState<AdSiteRow | null>(null);

  const list = useQuery({
    queryKey: ['network', 'sites', { q: params.get('q') ?? '', page }],
    queryFn: () => networkApi.sites({ q: params.get('q') || undefined, page, perPage: PER_PAGE }),
    placeholderData: keepPreviousData,
  });

  const data = list.data;

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">
            광고 사이트
            {list.isFetching && <span className="page-head__busy">갱신 중…</span>}
          </h1>
          <p className="page-desc">
            리퍼러 도메인의 분류표입니다. 리퍼러 목록·통계에서 도메인 옆에 보이는 사이트 타입이 이 표에서
            옵니다. 2023-03 이후 바뀐 것이 없습니다.
          </p>
        </div>
      </header>

      <div className="filters">
        <Field label="리퍼러 · 메모 등 검색">
          <SearchInput
            value={q}
            placeholder="도메인, 사이트타입, 메모, 파트너 코드"
            onChange={setQ}
          />
        </Field>
        <button
          type="button"
          className="btn btn--ghost filters__reset"
          onClick={() => setParams(new URLSearchParams(), { replace: true })}
        >
          조건 초기화
        </button>
      </div>

      {list.isPending ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      ) : data && data.items.length === 0 ? (
        <EmptyBox>조건에 맞는 광고 사이트가 없습니다.</EmptyBox>
      ) : (
        data && (
          <>
            <div className="table-wrap">
              <table className="table network-table">
                <thead>
                  <tr>
                    <th className="num">번호</th>
                    <th>리퍼러 도메인</th>
                    <th>사이트타입</th>
                    <th>광고 가능 타입</th>
                    <th>노출 광고 사이트</th>
                    <th>파트너 코드들</th>
                    <th>메모</th>
                    <th>등록일</th>
                    <th>상세</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row, i) => (
                    <tr key={row.uid}>
                      <td className="num td-muted">{descendingNumber(data.total, data.page, data.perPage, i)}</td>
                      <td>
                        <HostLink host={row.refhost} />
                      </td>
                      <td>{labelOf(HOST_TYPE_LABELS, row.hosttype)}</td>
                      <td>
                        <CodeBadges codes={row.bannertypes} labels={AD_TYPE_LABELS} />
                      </td>
                      <td className="td-clip" title={row.adsites ?? undefined}>
                        {truncate(row.adsites, 30)}
                      </td>
                      <td className="td-clip" title={row.partnerCodes ?? undefined}>
                        {truncate(row.partnerCodes, 30)}
                      </td>
                      <td className="td-clip td-muted" title={row.memo ?? undefined}>
                        {truncate(row.memo, 30)}
                      </td>
                      <td className="td-muted">{datetime(row.regdate)}</td>
                      <td>
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDetail(row)}>
                          상세
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              total={data.total}
              onChange={(p) => update({ page: p }, false)}
            />
          </>
        )
      )}

      {detail && (
        <DetailModal
          title={detail.refhost ?? `광고 사이트 #${detail.uid}`}
          onClose={() => setDetail(null)}
          fields={[
            { label: '리퍼러 도메인', value: <HostLink host={detail.refhost} /> },
            { label: '사이트타입', value: labelOf(HOST_TYPE_LABELS, detail.hosttype) },
            {
              label: '광고 가능 타입',
              value: <CodeBadges codes={detail.bannertypes} labels={AD_TYPE_LABELS} />,
            },
            { label: '노출 광고 사이트', value: text(detail.adsites), pre: true },
            { label: '파트너 코드들', value: text(detail.partnerCodes), pre: true },
            { label: '메모', value: text(detail.memo), pre: true },
            { label: '등록일', value: datetime(detail.regdate) },
          ]}
        />
      )}
    </section>
  );
}
